import { useState } from 'react'
import { X, AlertTriangle } from 'lucide-react'
import { useBrechasStore } from '../../store/brechasStore'
import { useAuthStore } from '../../store/authStore'

const BrechaForm = ({ criterioId, onClose, onSuccess }) => {
  const { createBrecha, isLoading, error } = useBrechasStore()
  const { token } = useAuthStore()

  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    severidad: 'media',
    fecha_deteccion: new Date().toISOString().split('T')[0]
  })

  const [validationErrors, setValidationErrors] = useState({})

  const validate = () => {
    const errors = {}

    if (!formData.titulo.trim()) {
      errors.titulo = 'El título es requerido'
    } else if (formData.titulo.length > 200) {
      errors.titulo = 'El título no puede exceder 200 caracteres'
    }

    if (!formData.descripcion.trim()) {
      errors.descripcion = 'La descripción es requerida'
    } else if (formData.descripcion.length > 2000) {
      errors.descripcion = 'La descripción no puede exceder 2000 caracteres'
    }

    if (!formData.fecha_deteccion) {
      errors.fecha_deteccion = 'La fecha de detección es requerida'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    try {
      await createBrecha({
        criterio_id: criterioId,
        ...formData
      }, token)

      if (onSuccess) onSuccess()
      onClose()
    } catch (err) {
      console.error('Error creating brecha:', err)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const getSeveridadColor = (sev) => {
    switch (sev) {
      case 'alta': return 'bg-red-100 text-red-800 border-red-300'
      case 'media': return 'bg-yellow-100 text-yellow-800 border-yellow-300'
      case 'baja': return 'bg-green-100 text-green-800 border-green-300'
      default: return 'bg-gray-100 text-gray-800 border-gray-300'
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            <h2 className="text-xl font-semibold">Registrar Nueva Brecha</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Título */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="titulo"
              value={formData.titulo}
              onChange={handleChange}
              className={`input-field ${validationErrors.titulo ? 'border-red-500' : ''}`}
              placeholder="Ej: Bajo seguimiento de egresados"
              maxLength={200}
            />
            {validationErrors.titulo && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.titulo}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              {formData.titulo.length}/200 caracteres
            </p>
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción <span className="text-red-500">*</span>
            </label>
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              rows={6}
              className={`input-field ${validationErrors.descripcion ? 'border-red-500' : ''}`}
              placeholder="Describe detalladamente la brecha o debilidad detectada..."
              maxLength={2000}
            />
            {validationErrors.descripcion && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.descripcion}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              {formData.descripcion.length}/2000 caracteres
            </p>
          </div>

          {/* Severidad */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Severidad <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-3">
              {['baja', 'media', 'alta'].map(sev => (
                <button
                  key={sev}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, severidad: sev }))}
                  className={`p-3 rounded-lg border-2 text-center font-medium capitalize transition-all ${
                    formData.severidad === sev
                      ? getSeveridadColor(sev)
                      : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {sev}
                </button>
              ))}
            </div>
          </div>

          {/* Fecha de Detección */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha de Detección <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="fecha_deteccion"
              value={formData.fecha_deteccion}
              onChange={handleChange}
              className={`input-field ${validationErrors.fecha_deteccion ? 'border-red-500' : ''}`}
            />
            {validationErrors.fecha_deteccion && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.fecha_deteccion}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Registrando...' : 'Registrar Brecha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default BrechaForm
